import { ContentViewedPageSource, contentViewedPageSource, routes } from '../constants';
import { getCurrentSearchId } from './ampli';
import { getPageSourceFromPathname } from './pageSource';

/**
 * Checks if a pathname is a search page
 * @param pathname - The pathname to check
 * @returns true if the pathname is a search page
 */
export const isSearchPage = (pathname: string): boolean => {
  return pathname.startsWith(`/${routes.search.root}`);
};

/**
 * Extracts the position from navigation state
 * @param state - The navigation state
 * @returns The position (1-indexed) if available, undefined otherwise
 */
export const getPositionFromState = (state: unknown): number | undefined => {
  return (state as { position?: number })?.position;
};

/**
 * Extracts position from URL query params (new-tab fallback).
 * Validates against NaN since query params are a system boundary.
 */
export const getPositionFromQuery = (query?: { position?: string }): number | undefined => {
  if (!query?.position) return undefined;
  const parsed = Number(query.position);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// Converts 0-indexed array index to 1-indexed display position.
export const indexToPosition = (index: number | undefined): number | undefined =>
  index !== undefined ? index + 1 : undefined;

/**
 * Gets the link props for a card, including the tracking context the target page
 * reads back out: a `page_source` param on every mapped origin page (SEAR-683),
 * plus search_id/position when navigating from search. The param is deliberately
 * NOT named `source` — attribution promotes a bare `source` URL param to
 * utm_source (SM-309, attributionAmplitude.ts), which would corrupt last-touch
 * marketing attribution.
 * Query params ensure values survive new-tab navigation, while state preserves
 * same-tab behavior. When `openInNewTab` is set on a search page, also embeds the
 * origin search URL as `from` so the new tab can hydrate its (per-tab) sessionStorage
 * with filters/tabKey/searchId — otherwise breadcrumb back to /suche would land on a
 * fresh search journey.
 */
export const getCardLinkProps = (
  itemLink: string,
  currentPathname: string,
  position?: number,
  options?: { currentSearch?: string; openInNewTab?: boolean },
): string | {
  pathname: string;
  query: Record<string, string>;
  state: { position?: number; pageSource?: ContentViewedPageSource };
} => {
  const source = getPageSourceFromPathname(currentPathname);
  if (!source) return itemLink;
  const onSearchPage = source === contentViewedPageSource.search;

  const [pathname, queryString] = itemLink.split('?');
  const existingParams = queryString
    ? Object.fromEntries(new URLSearchParams(queryString).entries())
    : {};
  const searchId = onSearchPage ? getCurrentSearchId() : undefined;
  const from = onSearchPage && options?.openInNewTab
    ? `${currentPathname}${options?.currentSearch || ''}`
    : undefined;

  return {
    pathname,
    query: {
      ...existingParams,
      ...(searchId && { search_id: searchId }),
      ...(onSearchPage && position !== undefined && { position: String(position) }),
      ...(from && { from }),
      ...(source && { page_source: source }),
    },
    state: {
      ...(onSearchPage && position !== undefined && { position }),
      ...(source && { pageSource: source }),
    },
  };
};
